import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import UserModal from '../components/UserModal';

// Icons
import { ReactComponent as EditIcon } from '../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as ChevronLeftIcon } from '../assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from '../assets/icons/chevron-right.svg';
import { ReactComponent as ChevronDoubleLeftIcon } from '../assets/icons/chevron-double-left.svg';
import { ReactComponent as ChevronDoubleRightIcon } from '../assets/icons/chevron-double-right.svg';
import { ReactComponent as SortUpIcon } from '../assets/icons/sort-up.svg';
import { ReactComponent as SortDownIcon } from '../assets/icons/sort-down.svg';
import { ReactComponent as SortIcon } from '../assets/icons/sort.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as CloseIcon } from '../assets/icons/close.svg';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Store all users for client-side filtering
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Sorting state
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const { userRole } = useAuth();
  const isAdmin = userRole === 'admin';
  
  // Fetch users on component mount or when pagination/sorting changes
  useEffect(() => {
    fetchUsers();
  }, [sortField, sortDirection]);
  
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      // Modified API call to include sorting parameters but not pagination
      // Since we're implementing client-side search, we need all users
      const response = await apiClient.get('/users', {
        params: {
          sortBy: sortField,
          sortDir: sortDirection
        }
      });
      
      // Handling response
      if (response.data.users) {
        // If API returns an object with users array
        setAllUsers(response.data.users);
      } else {
        // If API returns just an array of users
        setAllUsers(response.data);
      }
      
      setError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again later.');
      setAllUsers([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Filter and paginate users based on search term and current page
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return allUsers;
    }
    
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    
    return allUsers.filter(user => 
      (user.username && user.username.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (user.email && user.email.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (user.full_name && user.full_name.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (user.slack_id && user.slack_id.toLowerCase().includes(lowerCaseSearchTerm))
    );
  }, [allUsers, searchTerm]);
  
  // Calculate pagination based on filtered users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, pageSize]);
  
  // Update pagination info whenever filtered users change
  useEffect(() => {
    setTotalUsers(filteredUsers.length);
    setTotalPages(Math.ceil(filteredUsers.length / pageSize));
    
    // Reset to first page if current page is out of bounds
    if (currentPage > Math.ceil(filteredUsers.length / pageSize)) {
      setCurrentPage(1);
    }
  }, [filteredUsers, pageSize]);
  
  // Update displayed users whenever paginated users change
  useEffect(() => {
    setUsers(paginatedUsers);
  }, [paginatedUsers]);
  
  const handleSort = (field) => {
    // If clicking the same field, toggle direction
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as sort field with default asc direction
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };
  
  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };
  
  const renderSortIcon = (field) => {
    if (field !== sortField) {
      return <SortIcon className="w-4 h-4 ml-1 inline-block text-gray-400" />;
    }
    
    return sortDirection === 'asc' 
      ? <SortUpIcon className="w-4 h-4 ml-1 inline-block text-primary-600" />
      : <SortDownIcon className="w-4 h-4 ml-1 inline-block text-primary-600" />;
  };
  
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };
  
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };
  
  const handleAddUser = () => {
    setCurrentUser(null);
    setShowModal(true);
  };
  
  const handleEditUser = (user) => {
    setCurrentUser(user);
    setShowModal(true);
  };
  
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      await apiClient.delete(`/users/${userToDelete.id}`);
      
      // After successful delete, refresh the user list
      fetchUsers();
      
      setIsDeleteConfirmOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user. Please try again.');
    }
  };
  
  const handleModalSave = async (userData, isNew) => {
    try {
      if (isNew) {
        // Create new user
        await apiClient.post('/users', userData);
        // Refresh to reflect the changes
        fetchUsers();
      } else {
        // Update existing user
        await apiClient.put(`/users/${userData.id}`, userData);
        
        // Update in allUsers array
        setAllUsers(allUsers.map(user => 
          user.id === userData.id ? { ...user, ...userData } : user
        ));
      }
      setShowModal(false);
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Failed to save user. Please try again.');
      // The modal will handle its own loading state, so no need to modify it here
      throw error; // Re-throw to let the modal handle it
    }
  };
  
  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Generate page numbers for pagination control
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5; // Show at most 5 page numbers
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };
  
  // Column headers with sortable fields
  const columns = [
    { id: 'id', label: 'ID', sortable: true },
    { id: 'username', label: 'Username', sortable: true },
    { id: 'email', label: 'Email', sortable: true },
    { id: 'full_name', label: 'Full Name', sortable: true },
    { id: 'slack_id', label: 'Slack ID', sortable: true },
    { id: 'registration_date', label: 'Registration Date', sortable: true },
    { id: 'created_at', label: 'Created At', sortable: true },
    { id: 'actions', label: 'Actions', sortable: false }
  ];
  
  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Manage Users</h1>
        
        {isAdmin && (
          <button
            onClick={handleAddUser}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add New User</span>
          </button>
        )}
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* Search and pagination controls row */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
        {/* Search input */}
        <div className="relative w-full md:w-72">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search users..."
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              title="Clear search"
            >
              <CloseIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
            </button>
          )}
        </div>
        
        {/* Page size selector */}
        <div className="flex items-center">
          <label htmlFor="pageSize" className="mr-2 text-sm text-gray-600">
            Rows per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={handlePageSizeChange}
            className="border rounded-md border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>
      
      {/* Search results indicator */}
      {searchTerm && (
        <div className="mb-4 text-sm text-gray-600">
          Found {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} matching "{searchTerm}"
        </div>
      )}
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map(column => (
                    <th 
                      key={column.id}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {column.sortable ? (
                        <button 
                          className="flex items-center cursor-pointer focus:outline-none"
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                          {renderSortIcon(column.id)}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      {searchTerm ? 'No matching users found' : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.full_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.slack_id || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.registration_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-primary-600 hover:text-primary-900 transition-colors"
                            title="Edit User"
                          >
                            <EditIcon className="w-5 h-5" />
                          </button>
                          
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteClick(user)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="Delete User"
                            >
                              <DeleteIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination controls */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {users.length ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
            </div>
            <div className="flex items-center space-x-2">
              {/* First Page Button */}
              <button 
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:bg-primary-50'}`}
                title="First Page"
              >
                <ChevronDoubleLeftIcon className="w-5 h-5" />
              </button>
              
              {/* Previous Page Button */}
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:bg-primary-50'}`}
                title="Previous Page"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              
              {/* Page Number Buttons */}
              {getPageNumbers().map(pageNumber => (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-3 py-1 rounded-md ${
                    pageNumber === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-primary-50'
                  }`}
                >
                  {pageNumber}
                </button>
              ))}
              
              {/* Next Page Button */}
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:bg-primary-50'}`}
                title="Next Page"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
              
              {/* Last Page Button */}
              <button 
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:bg-primary-50'}`}
                title="Last Page"
              >
                <ChevronDoubleRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* User Modal for Add/Edit */}
      {showModal && (
        <UserModal
          user={currentUser}
          onClose={() => setShowModal(false)}
          onSave={handleModalSave}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete user <span className="font-semibold">{userToDelete?.username}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteConfirmOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;